import {
	AnonymousStudentProfileSynced,
	AnonymousStudentServiceReady,
	IAnonymousStudentService,
	StudentRepositoryStateType,
} from '@studyportals/anonymous-student-interfaces';

import {
	ISessionService,
	IWishlistService,
	SessionServiceReadyEvent,
	WishlistServiceReadyEvent,
	IUser as ISession
} from '@studyportals/student-interfaces';

import {
	IEventAggregationService,
	ISubscriber,
} from '@studyportals/event-aggregation-service-interface';

import { StudentField } from '@studyportals/studentdomain';
import { IExperiment, IUser, StudentDataRequiredServicesReady } from '@/types';
import { User, WishlistItem } from '@/domain';
import { DataLayerUserObjectReady } from '@/domain';
import {
	EventDispatcher,
	InternalEventDispatcher,
} from '@/infrastructure/outbound';
import { StudentRegistrationData } from './types/student-registration-data';

export class StudentDataAggregationService implements ISubscriber<StudentDataRequiredServicesReady | AnonymousStudentProfileSynced> {
	private student: IUser;
	private eventDispatcher: InternalEventDispatcher;
	private eventAggregationService: IEventAggregationService = window[
		'EventAggregationService'
	] as IEventAggregationService;

	private anonymousStudentService?: IAnonymousStudentService;
	private wishlistService?: IWishlistService;
	private sessionService?: ISessionService;

	private provider?: string;
	private userRegistered = false;
	private registrationEventTriggered = false;
	private updateEventTriggered = false;

	constructor() {
		this.student = new User();

		this.eventDispatcher = new EventDispatcher();

		this.eventAggregationService.subscribeTo(
			AnonymousStudentServiceReady.EventType,
			this,
			true,
		);
		this.eventAggregationService.subscribeTo(
			WishlistServiceReadyEvent.EventType,
			this,
			true,
		);
		this.eventAggregationService.subscribeTo(
			SessionServiceReadyEvent.EventType,
			this,
			true,
		);
		this.eventAggregationService.subscribeTo(
			AnonymousStudentProfileSynced.EventType,
			this,
			true,
		);

		this.listenToStudentRegistration();
		this.listenToExperimentInitiation();
	}

	async notify(
		event: StudentDataRequiredServicesReady | AnonymousStudentProfileSynced,
	): Promise<void> {
		if (event.eventType === AnonymousStudentServiceReady.EventType) {
			this.setAnonymousStudentService(event as AnonymousStudentServiceReady);
		}

		if (event.eventType === WishlistServiceReadyEvent.EventType) {
			this.setWishlistService(event as WishlistServiceReadyEvent);
		}

		if (event.eventType === SessionServiceReadyEvent.EventType) {
			this.setSessionService(event as SessionServiceReadyEvent);
		}

		if (event.eventType === AnonymousStudentProfileSynced.EventType) {
			if (
				(event as AnonymousStudentProfileSynced).state !==
				StudentRepositoryStateType.ONLINE ||
				!this.userRegistered
			) {
				return;
			}

			const studentData = await this.getStudentRegistrationData();

			if (studentData) {
				this.eventDispatcher.dispatchRegistrationEvent(studentData);

				this.registrationEventTriggered = true;
			}
		}

		if (
			this.anonymousStudentService &&
			this.wishlistService &&
			this.sessionService
		) {
			await this.setStudentData();
			await this.setWishlist();

			const sessionData = await this.getSessionData();
			if (sessionData) {
				this.setSessionData(sessionData);

				const hashedStudentId = await this.hash(sessionData.identityId);

				if (this.registrationEventTriggered && !this.updateEventTriggered) {
					this.eventDispatcher.dispatchRegistrationUpdateEvent(
						this.student,
						hashedStudentId
					);

					this.updateEventTriggered = true;
				}
			}

			this.notifyStudentObjectReady();
		}
	}

	private setAnonymousStudentService(
		event: AnonymousStudentServiceReady,
	): void {
		this.anonymousStudentService = event.anonymousStudentService;
	}

	private async setStudentData(): Promise<void> {
		if (!this.anonymousStudentService) {
			return;
		}

		try {
			const studentData = await this.anonymousStudentService.getStudentData([
				StudentField.EMAIL,
				StudentField.GENDER,
				StudentField.BIRTH_DATE,
				StudentField.NATIONALITY_COUNTRY_ISO,
				StudentField.RESIDENCE_COUNTRY_ID,
				StudentField.INTERESTS_DISCIPLINES,
				StudentField.INTERESTS_COUNTRIES,
				StudentField.START_PERIOD_DATE,
				StudentField.ATTENDANCE,
				StudentField.CURRENCY,
				StudentField.TUITION_BUDGET,
				StudentField.LIVING_BUDGET,
				StudentField.STUDY_LEVEL,
				StudentField.STUDY_COUNTRY_ID,
				StudentField.ORIGIN_ORGANISATION_ID,
				StudentField.DISCIPLINES,
				StudentField.GPA,
				StudentField.WORK_EXPERIENCE,
				StudentField.EMAILING_FAVOURITES,
				StudentField.PROFICIENCY_TYPE,
			]);

			this.student.setStudentData(studentData);
		} catch (error) {
			console.error(
				"StudentDataManager: Failed to retrieve student's data.",
				error,
			);
		}
	}

	private setWishlistService(event: WishlistServiceReadyEvent): void {
		this.wishlistService = event.wishlistService;
	}

	private async setWishlist(): Promise<void> {
		if (!this.wishlistService) {
			return;
		}

		const wishlist = await this.wishlistService.getWishlist();

		const favourites = wishlist.favourites.map((f) => {
			return new WishlistItem(f.study.id, f.study.card?.getTitle());
		});

		this.student.setWishlist(favourites);
	}

	private setSessionService(event: SessionServiceReadyEvent): void {
		this.sessionService = event.sessionService;
	}

	private setSessionData(session: ISession): void {
		this.student.setSessionData(
			session.email ? true : false,
			this.isStudyportalsEmployee(session.email),
			session.identityId
		);
	}

	private async getSessionData(): Promise<ISession | undefined> {
		if (!this.sessionService) {
			return;
		}

		const session = await this.sessionService.getSession();
		if (!session) {
			return;
		}

		return session.getUser();
	}

	private notifyStudentObjectReady(): void {
		const event = new DataLayerUserObjectReady(this.student);
		this.eventAggregationService.publishTo(
			DataLayerUserObjectReady.EventType,
			event,
		);
	}

	private async getStudentRegistrationData(): Promise<StudentRegistrationData | undefined> {
		if (!this.anonymousStudentService) {
			return;
		}

		const data = await this.anonymousStudentService.getStudentData([
			StudentField.EMAIL,
			StudentField.REFERRER,
		]);

		const email = data[StudentField.EMAIL];

		return {
			isStudyportalsEmployee: email ? this.isStudyportalsEmployee(email) : false,
			referrer: data[StudentField.REFERRER],
			provider: this.provider
		};
	}

	private listenToStudentRegistration(): void {
		document.addEventListener('student-registered', (e) => {
			const event = e as CustomEvent<{
				provider: string;
			}>;

			this.provider = event.detail.provider;
			this.userRegistered = true;
		});
	}

	private readActiveExperiments(): void {
		const active_experiments = sessionStorage.getItem('sp_active_experiments');
		if (active_experiments !== null)
			this.student.abTests = JSON.parse(active_experiments) as IExperiment[];
	}

	private listenToExperimentInitiation(): void {
		document.addEventListener('dataLayer-sp-experiment-initiated', () => {
			this.readActiveExperiments();
		});
		this.readActiveExperiments();
	}

	private isStudyportalsEmployee(email: string): boolean {
		return email.split('@')[1] === 'studyportals.com';
	}

	private async hash(value: string): Promise<string> {
		const msgBuffer = new TextEncoder().encode(value);

		const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
		const hashArray = Array.from(new Uint8Array(hashBuffer));
		const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

		return hashHex;
	}
}
